<script>
	import { onDestroy, onMount } from "svelte";
	import { Col, Row } from "@sveltestrap/sveltestrap";
	import CustomCollapseCard from "../CustomCollapseCard/CustomCollapseCard.svelte";
	import CompareReportsCard from "./CompareReportsCard.svelte";
	import {
		getCaseDetails,
		getReportDetails,
		getCaseReports,
	} from "src/services/fmsTasking";
	import { splitCamelCaseAndCapitalize } from "src/utils/misc";
	import { damageReviewedData } from "src/store/store";

	export let caseId; // ID of the case to compare reports for
	export let state = "view"; // state of the component, e.g., 'view' or 'edit'
	export let caseManagement = false;
	export let status = "";

	let reportOptions = []; // holds the list of available reports for selection.
	let leftData = {}; // data for the left-side report card.
	let rightData = {}; // data for the right-side report card.
	let vehicleId = null; // the ID of the vehicle associated with the case.
	let lastEvaluatedKey = ""; // for pagination when fetching report options.
	let page = 1; // current page number for report options pagination.
	let totalPage = 1; // total number of pages for report options.
	let leftSelectedOption = null; // the selected report option for the left card.
	let rightSelectedOption = null; // the selected report option for the right card.
	$: editablePanel = ""; // whether the report selection panels should be editable, other is disabled (e.g., in 'view' state).

	$: isInvestigating = status === "Investigating";
	$: isApproving = status === "Approving" && state === "edit";
	$: isRadioButtonsEnabled = isInvestigating || isApproving; // radio buttons are enabled if the state is not 'view'.

	/**
	 * Fetches report options for a given vehicle, with support for pagination.
	 * @param {string} vId - The vehicle ID.
	 * @param {boolean} [reset=false] - If true, resets pagination and clears existing options.
	 */
	async function fetchReportOptions(vId, reset = false) {
		try {
			if (reset) {
				page = 1;
				reportOptions = [];
				totalPage = 1;
				lastEvaluatedKey = "";
			}

			// stop fetching if there are no more pages
			if (lastEvaluatedKey === null && !reset) {
				return;
			}

			const res = await getCaseReports(caseId, vId, lastEvaluatedKey);
			const reorderedData = res?.data?.data;
			lastEvaluatedKey = res?.data?.lastEvaluatedKey;
			const list = reorderedData ?? [];

			// format the fetched data into options for the select dropdown
			const newOptions = list.map((item, idx) => ({
				value: reportOptions.length + idx,
				label: `Report ${item.count.split("#")[0]} – ${
					item.bookingNo
				} ${splitCamelCaseAndCapitalize(item.type)}`,
				bookingId: item.bookingId,
				bookingNo: item.bookingNo,
				type: item.type,
			}));

			reportOptions = [...reportOptions, ...newOptions];
			totalPage = res?.data?.data?.totalPage ?? 1;
			page++;
		} catch (err) {
			console.error("Failed to fetch report options:", err);
		}
	}

	/**
	 * Fetches details for a selected report and updates the corresponding side of the comparison.
	 * @param {object} option - The selected report option.
	 * @param {'left' | 'right'} side - The side of the comparison to update.
	 */
	async function handleReportChange(option, side) {
		if (!option) return;

		try {
			const res = await getReportDetails(caseId, {
				vehicleId: vehicleId,
				...option,
			});

			let data = normalizeReportData(res?.data);
			data.isLastReviewed = res?.data?.isLastReviewed;

			// check the local store for any reviewed data that might override the fetched data
			const existingIndex = $damageReviewedData.findIndex(
				(item) =>
					item.bookingId === data.bookingId &&
					item.reportType === data.reportType
			);
			if (existingIndex !== -1) {
				data = $damageReviewedData[existingIndex];
			}

			// update the state for the corresponding side
			if (side === "left") {
				leftData = data;
				leftSelectedOption = option;
			} else {
				rightData = data;
				rightSelectedOption = option;
			}
		} catch (e) {
			console.error(`Failed to load ${side} report`, e);
		}
	}

	/**
	 * Normalizes the report data structure for consistency.
	 * @param {object} data - The raw report data from the API.
	 * @returns {object} The normalized report data.
	 */
	function normalizeReportData(data) {
		if (!data) return {};
		// standardize image property
		if (data.bookingImages) {
			data.images = data.bookingImages;
			delete data.bookingImages;
		}
		// ensure reportType and bookingId fields exist
		data.reportType = data.type || data.reportType;
		data.bookingId = data.bookingId || data.bookingNo;
		return data;
	}

	/**
	 * initializes the component by fetching case details and pre-loading the first two reports for comparison.
	 */
	async function initData() {
		try {
			const res = await getCaseDetails(caseId);
			const data = res?.data;

			vehicleId = data?.vehicleId;

			if (vehicleId) {
				await fetchReportOptions(vehicleId, true);

				// default to showing the first report on the right
				if (reportOptions.length > 0) {
					rightSelectedOption = reportOptions[0];
					handleReportChange(rightSelectedOption, "right");
				}

				// default to showing the second report on the left, if available
				if (reportOptions.length > 1) {
					leftSelectedOption = reportOptions[1];
					handleReportChange(leftSelectedOption, "left");
				}
			}
		} catch (error) {
			console.error(error);
		}
	}

	onMount(() => {
		if (caseId) {
			initData();
		}
	});

	onDestroy(() => {
		editablePanel = "";
	});
</script>

<CustomCollapseCard
	title="Compare Reports"
	isOpenDefault={true}
	customClass="mb-4"
>
	<div class="compare-reports">
		<!-- grid layout for the side-by-side report cards -->
		<div class="compare-grid">
			<Row class="w-100 g-0 align-items-stretch">
				<Col md={6} class="d-flex">
					<div class="card-wrapper">
						<!-- left report card -->
						<CompareReportsCard
							data={leftData}
							options={reportOptions}
							value={leftSelectedOption}
							{isRadioButtonsEnabled}
							{caseManagement}
							onSelectReport={(opt) =>
								handleReportChange(opt, "left")}
							onLoadMore={() => fetchReportOptions(vehicleId)}
							side="left"
							{editablePanel}
							on:cardActive={(e) => {
								if (
									e.detail.data.every(
										(item) =>
											item.damageAttribute === "none" ||
											item.damageAttribute === null
									)
								) {
									editablePanel = "";
								} else {
									editablePanel = leftData.count;
								}
							}}
						/>
					</div>
				</Col>
				<Col md={6} class="d-flex">
					<div class="card-wrapper">
						<!-- right report card -->
						<CompareReportsCard
							data={rightData}
							options={reportOptions}
							value={rightSelectedOption}
							{isRadioButtonsEnabled}
							{caseManagement}
							onSelectReport={(opt) =>
								handleReportChange(opt, "right")}
							onLoadMore={() => fetchReportOptions(vehicleId)}
							side="right"
							{editablePanel}
							on:cardActive={(e) => {
								if (
									e.detail.data.every(
										(item) =>
											item.damageAttribute === "none" ||
											item.damageAttribute === null
									)
								) {
									editablePanel = "";
								} else {
									editablePanel = rightData.count;
								}
							}}
						/>
					</div>
				</Col>
			</Row>
		</div>
	</div>
</CustomCollapseCard>

<style>
	.compare-reports {
		display: flex;
		flex-direction: column;
		gap: 12px;
	}

	.compare-grid {
		position: relative;
		padding-top: 0;
		padding-bottom: 36px;
	}

	/* vertical line that separates the two cards */
	.compare-grid::after {
		content: "";
		position: absolute;
		top: 32px;
		bottom: 32px;
		left: 50%;
		width: 1px;
		background: var(--fds-outline-variant);
		z-index: 1;
	}

	.card-wrapper {
		width: 100%;
		display: flex;
		flex: 1;
		padding: 0 12px;
	}
</style>
